import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Bump this whenever the cookie policy or the set of categories changes.
 * A stored consent record whose `policyVersion` no longer matches this
 * constant is treated as unanswered, so the banner re-appears.
 */
export const CONSENT_POLICY_VERSION = 1

/** Consent expires after 6 months, per CNIL guidance. */
const CONSENT_VALIDITY_MS = 6 * 30 * 24 * 60 * 60 * 1000

export type ConsentCategory = 'necessary' | 'media'

export interface ConsentCategories {
  /** Always on — covers the consent record itself. Not toggleable. */
  necessary: true
  /** Third-party embedded media (YouTube, Vimeo, maps…) — for future use. */
  media: boolean
}

interface ConsentState {
  categories: ConsentCategories
  consentedAt: string | null
  policyVersion: number
  /** Controls the preferences dialog ("Customize") — see CookiePreferencesDialog. */
  isPreferencesOpen: boolean

  acceptAll: () => void
  refuseAll: () => void
  setCategory: (category: Exclude<ConsentCategory, 'necessary'>, value: boolean) => void
  /** Records the currently-selected categories as the consent decision (the "Customize" panel's save action). */
  savePreferences: () => void
  isExpired: () => boolean
  openPreferences: () => void
  closePreferences: () => void
}

function hasRespondedAndValid(state: Pick<ConsentState, 'consentedAt' | 'policyVersion'>) {
  if (state.consentedAt === null) return false
  if (state.policyVersion !== CONSENT_POLICY_VERSION) return false

  const consentedAtMs = new Date(state.consentedAt).getTime()
  if (Number.isNaN(consentedAtMs)) return false

  return Date.now() - consentedAtMs < CONSENT_VALIDITY_MS
}

export const useConsentStore = create<ConsentState>()(
  persist(
    (setState, getState) => ({
      categories: { necessary: true, media: false },
      consentedAt: null,
      policyVersion: CONSENT_POLICY_VERSION,
      isPreferencesOpen: false,

      acceptAll: () =>
        setState({
          categories: { necessary: true, media: true },
          consentedAt: new Date().toISOString(),
          policyVersion: CONSENT_POLICY_VERSION,
          isPreferencesOpen: false,
        }),

      refuseAll: () =>
        setState({
          categories: { necessary: true, media: false },
          consentedAt: new Date().toISOString(),
          policyVersion: CONSENT_POLICY_VERSION,
          isPreferencesOpen: false,
        }),

      setCategory: (category, value) =>
        setState((current) => ({
          categories: { ...current.categories, [category]: value },
        })),

      savePreferences: () =>
        setState({
          consentedAt: new Date().toISOString(),
          policyVersion: CONSENT_POLICY_VERSION,
          isPreferencesOpen: false,
        }),

      isExpired: () => !hasRespondedAndValid(getState()),

      openPreferences: () => setState({ isPreferencesOpen: true }),
      closePreferences: () => setState({ isPreferencesOpen: false }),
    }),
    {
      name: 'cookie-consent',
      partialize: (state) => ({
        categories: state.categories,
        consentedAt: state.consentedAt,
        policyVersion: state.policyVersion,
      }),
    },
  ),
)

/** True once the visitor has answered with a still-valid, current-policy consent. */
export const selectHasRespondedAndValid = (state: ConsentState) => hasRespondedAndValid(state)
export const selectConsentCategories = (state: ConsentState) => state.categories
export const selectIsPreferencesOpen = (state: ConsentState) => state.isPreferencesOpen
